import { useCallback, useContext, useEffect, useState } from 'react';
import ViewVehiclesModal from '@/new-cars/Modals/ViewVehiclesModal';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConfiguration } from '~/context/common/StaticContext';
import { useModalContext } from '~/context/new-cars/ModalContext/ModalContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import { PriceBreakdownContextProvider } from '~/context/new-cars/PriceBreakdownContext';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import Footer from './Footer';
import PriceBreakdownModal from './PriceBreakdownModal';

function useShouldYieldToSiteFooter(footerElement: HTMLElement | null) {
  const isMobile = useIsMobile();
  const [shouldYield, setShouldYield] = useState(false);

  useEffect(() => {
    if (!isMobile || !footerElement) {
      setShouldYield(false);

      return;
    }

    const element = footerElement;
    let timeout = 0;

    function onScroll() {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        // visualViewport accounts for mobile browser toolbar resizing
        const viewportHeight =
          window.visualViewport?.height ?? window.innerHeight;

        setShouldYield(
          element.getBoundingClientRect().bottom < viewportHeight - 1
        );
      }, 50);
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', onScroll);
    };
  }, [footerElement, isMobile]);

  return shouldYield;
}

type OwnProps = {
  showHero: boolean;
};

export default function StickyFooter({ showHero }: OwnProps) {
  const { activeSection, isFirstSection } = useContext(ParentModelContext);
  const { enableNewCarConfiguratorV2 } = useConfiguration();
  const isMobile = useIsMobile();
  const closestVehicle = useClosestVehicle();
  const testDriveCenter = useTestDriveCenter();
  const { setPriceBreakdownModalOpen } = useModalContext();

  const showViewVehicles =
    enableNewCarConfiguratorV2 && !testDriveCenter.isActive;

  const [footerElement, setFooterElement] = useState<HTMLDivElement | null>(
    null
  );

  const open = useCallback(
    () => setPriceBreakdownModalOpen(true),
    [setPriceBreakdownModalOpen]
  );

  const shouldYield = useShouldYieldToSiteFooter(footerElement);

  useEffect(() => {
    if (!closestVehicle?.vehicleId) return;

    document.documentElement.classList.add('with-adjusted-chat-icon');

    return () => {
      document.documentElement.classList.remove('with-adjusted-chat-icon');
    };
  }, [closestVehicle?.vehicleId]);

  const heroReplacesFooter = isMobile && showHero;
  const showFooter =
    !isFirstSection(activeSection) ||
    (enableNewCarConfiguratorV2 && !heroReplacesFooter);

  const footer = (
    <Footer
      footerRef={setFooterElement}
      isReleased={shouldYield}
      onOpen={open}
    />
  );

  return showFooter ? (
    <>
      {showViewVehicles && <ViewVehiclesModal />}
      {testDriveCenter.isActive ? (
        footer
      ) : (
        <PriceBreakdownContextProvider>
          <PriceBreakdownModal />
          {footer}
        </PriceBreakdownContextProvider>
      )}
    </>
  ) : null;
}
